import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { OkResponse, UserDto } from "@reina/shared";
import type { LoginInput, User } from "@reina/shared";
import { api, ApiError } from "./client";

/** `{user: UserDto}` envelope used by login + me per the contract. */
const UserEnvelope = z.object({ user: UserDto });

export const meKey = ["auth", "me"] as const;

/**
 * Current session user. Resolves to `null` (not an error) when the session is
 * missing/expired so route guards can branch on data alone.
 */
export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: async (): Promise<User | null> => {
      try {
        const { user } = await api.get("/auth/me", { schema: UserEnvelope });
        return user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => api.post("/auth/login", input, { schema: UserEnvelope }),
    onSuccess: ({ user }) => {
      queryClient.setQueryData(meKey, user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/auth/logout", undefined, { schema: OkResponse }),
    onSuccess: () => {
      queryClient.setQueryData(meKey, null);
      // Drop everything else so the next login starts from a clean cache.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
    },
  });
}
